import {
  ROBINHOOD_EXPLORER,
  ROBINHOOD_RPC,
  WETH_ADDRESS,
  type LaunchSummary
} from "@bubblefun/shared";

type Address = `0x${string}`;

/**
 * Explicitly allowlisted, read-only markets used to exercise the public-RPC
 * chart adapter. They are not Bubble Hood launches and must never be routed
 * through BubbleZap, boosts, graduation, creator fees, or holder fallbacks.
 */
export interface ExternalMarketDefinition {
  readonly origin: "external";
  readonly source: "public-rpc-test";
  readonly readOnly: true;
  readonly address: Address;
  readonly pool: Address;
  readonly name: string;
  readonly symbol: string;
  readonly description: string;
  readonly tokenDecimals: number;
  readonly totalSupplyTokens: number;
  readonly tokenIs0: boolean;
  /** Uniswap V3 fee, in hundredths of a basis point (1_000_000 = 100%). */
  readonly poolFeePips: number;
  readonly createdAt: string | null;
}

export interface ExternalLaunchSummary extends LaunchSummary {
  readonly origin: "external";
  readonly dataMode: "public-rpc-test";
  readonly readOnly: true;
  readonly tokenDecimals: number;
  readonly totalSupplyTokens: number;
  readonly tokenIs0: boolean;
  readonly poolFeePips: number;
  /** False until a public-RPC refresh has returned current onchain values. */
  readonly rpcAvailable: boolean;
  /** External test entries do not currently claim indexed 24-hour coverage. */
  readonly change24hAvailable: false;
  readonly lastRpcBlock: number | null;
  readonly lastRpcUpdate: string | null;
  readonly wethUsd: number | null;
  readonly tokenExplorerUrl: string;
  readonly poolExplorerUrl: string;
}

export const EXTERNAL_MARKET_RPC = ROBINHOOD_RPC;

export const WETH_USD_REFERENCE = {
  pool: "0x52e65B17fB6E5BA00Ed806f37Afcd2DaA50271Ca" as Address,
  weth: WETH_ADDRESS as Address,
  usdToken: "0x5fc5360d0400a0fd4f2af552add042d716f1d168" as Address,
  wethDecimals: 18,
  usdDecimals: 6,
  wethIs0: true,
  label: "USDG/WETH onchain reference"
} as const;

export const EXTERNAL_MARKETS: readonly ExternalMarketDefinition[] = [
  {
    origin: "external",
    source: "public-rpc-test",
    readOnly: true,
    address: "0x020bfC650A365f8BB26819deAAbF3E21291018b4",
    pool: "0xA70fc67C9F69da90B63a0e4C05D229954574E313",
    name: "Cash Cat",
    symbol: "CASHCAT",
    description: "Verified Cash Cat/WETH V3 market, tracked read-only through Robinhood Chain's public RPC.",
    tokenDecimals: 18,
    totalSupplyTokens: 1_000_000_000,
    tokenIs0: true,
    poolFeePips: 10_000,
    createdAt: "2026-06-18T20:01:25.000Z"
  },
  {
    origin: "external",
    source: "public-rpc-test",
    readOnly: true,
    address: "0x0b1f28F792803C43a2e22FAdea62B4c55F1dCf5F",
    pool: "0xbeed7d18ea80bf36e85037f083fd12122b646122",
    name: "Buff Cat",
    symbol: "BUFFCAT",
    description: "Allowlisted external BUFFCAT/WETH V3 market for read-only public-RPC chart testing.",
    tokenDecimals: 18,
    totalSupplyTokens: 1_000_000_000,
    tokenIs0: true,
    poolFeePips: 100,
    createdAt: null
  },
  {
    origin: "external",
    source: "public-rpc-test",
    readOnly: true,
    address: "0x610940d78155239a0Ac4dcF74eCF9FDb603cdCa1",
    pool: "0x0d2016beb9b012f291c70115233952a8701fdc88",
    name: "Robinhood Cats",
    symbol: "CATS",
    description: "Allowlisted external WETH/CATS V3 market for read-only public-RPC chart testing.",
    tokenDecimals: 18,
    totalSupplyTokens: 1_000_000_000,
    tokenIs0: false,
    poolFeePips: 10_000,
    createdAt: null
  }
] as const;

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000" as Address;

function seedExternalSummary(market: ExternalMarketDefinition): ExternalLaunchSummary {
  return {
    address: market.address,
    creator: ZERO_ADDRESS,
    pool: market.pool,
    positionId: "external-read-only",
    name: market.name,
    symbol: market.symbol,
    description: market.description,
    logoUri: "",
    website: "",
    twitter: "",
    telegram: "",
    // Zero means unavailable until refreshExternalMarketSummaries succeeds.
    // Consumers must gate display on rpcAvailable rather than presenting zero.
    priceEth: 0,
    fdvEth: 0,
    marketCapUsd: null,
    volume24hEth: 0,
    volume24hUsd: null,
    change24h: 0,
    marketProgress: 0,
    holders: 0,
    createdAt: market.createdAt ?? "",
    status: "active",
    origin: market.origin,
    dataMode: market.source,
    readOnly: market.readOnly,
    tokenDecimals: market.tokenDecimals,
    totalSupply: market.totalSupplyTokens,
    totalSupplyTokens: market.totalSupplyTokens,
    tokenIs0: market.tokenIs0,
    poolFeePips: market.poolFeePips,
    rpcAvailable: false,
    change24hAvailable: false,
    lastRpcBlock: null,
    lastRpcUpdate: null,
    wethUsd: null,
    tokenExplorerUrl: `${ROBINHOOD_EXPLORER}/address/${market.address}`,
    poolExplorerUrl: `${ROBINHOOD_EXPLORER}/address/${market.pool}`
  };
}

export const EXTERNAL_MARKET_SEEDS: readonly ExternalLaunchSummary[] = EXTERNAL_MARKETS.map(seedExternalSummary);

export function getExternalMarketDefinition(address: string): ExternalMarketDefinition | undefined {
  const normalized = address.toLowerCase();
  return EXTERNAL_MARKETS.find((market) => market.address.toLowerCase() === normalized);
}

export function getExternalMarketSeed(address: string): ExternalLaunchSummary | undefined {
  const normalized = address.toLowerCase();
  return EXTERNAL_MARKET_SEEDS.find((market) => market.address.toLowerCase() === normalized);
}
